// Écran « Planifier » (lot B3) : l'auto-planification n'est pas une fenêtre, c'est un travail. Un écran entier
// (feuille pleine hauteur sous 768 px) lu en trois questions — quoi planifier, à quel rythme, à partir de quand —
// en sections fermées par un filet, chaque réglage expliqué en une phrase (la grammaire du panneau « Ajuster le
// planning », lot B2). Le rail bas porte le résumé et l'unique action pleine.
//
// Geste signature : le résumé se recompte. À chaque réglage touché, l'écran rejoue `construirePlanning` — la même
// fonction que le bouton — sur une COPIE de l'état, et réécrit la ligne du bas. Les items de partiel qui
// arriveraient trop tard s'annoncent AVANT le lancement, là où l'ancienne fenêtre les jetait en alert() après coup.
//
// Rien n'est recalculé ici : les réglages, les préréglages, le réglage par l'IA, l'ordre des matières et le calcul
// lui-même restent ceux de src/domain/scheduler/autoplan.js. Cet écran ne fait que les rendre et les appeler, et
// `triggerAutoPlan` est repris à l'identique (même payload save_tasks) avec dialogue.confirmer à la place de confirm().
import { html, attr } from '../../core/html.js';
import { state } from '../../core/store.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';
import { ouvrirMenu } from '../components/menu.js';

var iaOuvert = false;        // la carte « Laisser l'IA régler » (repliée par défaut)
var ouvertureDepuis = null;  // l'élément qui a ouvert l'écran : le focus y retourne à la fermeture
var defilement = 0;          // position du corps, rendue après chaque redessin
var focusId = '';            // identifiant de l'élément focalisé, rendu après chaque redessin

// ---------------------------------------------------------------- petites aides
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
function nombre(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' '); }
function specs2() { return state.selectedSpecs2 || []; }
function seuil() { var n = parseInt(state.regroupSeuilMots, 10); return (!n || n < 200) ? 3000 : n; }
// Le compte est mis en cache, mais le catalogue bouge sous l'écran : « Regrouper » et « Rétablir les découpages »
// refont `window.ednsItems` pendant que l'écran reste ouvert. La clé est sa taille, et l'ouverture vide le cache.
function compteParMatiere() {
    var n = (window.ednsItems || []).length;
    if (compteParMatiere.cache && compteParMatiere.cleN === n) return compteParMatiere.cache;
    var c = {};
    (window.ednsItems || []).forEach(function(i) {
        c[i.matiere] = (c[i.matiere] || 0) + 1;
        (i.collegesSecondaires || []).forEach(function(sc) { if (sc !== i.matiere) c[sc] = (c[sc] || 0) + 1; });
    });
    compteParMatiere.cleN = n;
    compteParMatiere.cache = c;
    return c;
}

// ---------------------------------------------------------------- l'aperçu : l'ordonnanceur rejoué pour de vrai
// Sur une copie de l'état ET des tâches : en mode « ajouter », le lissage réécrit les dates des tâches qu'on lui
// passe, et ce sont les vraies séances de l'étudiant. La copie les met hors d'atteinte.
var apercu = null, apercuCle = null, apercuMinuteur = null, apercuEnCours = false;

function cleReglages() {
    var s = state;
    return JSON.stringify([s.selectedSpecs, s.orderedSpecialties, s.selectedSpecs2, s.orderedSpecialties2, s.deuxiemeLot,
        s.configStartDate, s.configStartDate2, s.itemsPerDay, s.maxJLimit, s.isLoopMode, s.sortStrategy, s.appendMode,
        s.doublePediatrie, s.doublePediatrie2, s.useSubPartsInAutoPlan, s.blocDepuisJ, s.chargeTotaleParJour,
        s.plafondJ0ParJour, s.plafondRevisionsParJour, s.pasJ0, s.spacingMode, s.spacingCommun,
        (s.tasks || []).length, (s.exams || []).map(function(e) { return e.id + ':' + (e.jCible || 0); })]);
}

function calculerApercu() {
    var cle = cleReglages();
    if (apercu && apercuCle === cle) return apercu;
    apercuCle = cle;
    if (!(state.selectedSpecs || []).length) { apercu = { rien: 'matieres' }; return apercu; }
    if (state.deuxiemeLot && !specs2().length) { apercu = { rien: 'matieres2' }; return apercu; }
    try {
        var copie = Object.assign({}, state, { tasks: (state.tasks || []).map(function(t) { return Object.assign({}, t); }) });
        var r = window.construirePlanning(copie, { items: window.ednsItems, now: function() { return 0; } });
        var avant = {};
        (state.tasks || []).forEach(function(t) { avant[t.id] = 1; });
        var neuves = r.tasks.filter(function(t) { return !avant[t.id]; });
        var dates = neuves.map(function(t) { return t.date; }).sort();
        var items = {};
        neuves.forEach(function(t) { items[String(t.baseItemId || t.itemId).replace(/_p\d+.*$/, '')] = 1; });
        apercu = {
            items: Object.keys(items).length, neuves: neuves.length, total: r.tasks.length,
            debut: dates[0] || '', fin: dates[dates.length - 1] || '',
            enRetard: (r.enRetard || []).filter(function(it, i, a) { return a.indexOf(it) === i; })
        };
    } catch (e) {
        apercu = { erreur: e && e.message ? e.message : 'calcul impossible' };
    }
    return apercu;
}

// Le corps du résumé, réécrit seul dans le rail bas : aucun redessin de l'écran pour une ligne de chiffres.
function resumeHtml() {
    if (apercuEnCours) return html`<span class="puce puce-attente ap-resume-etat">Calcul en cours</span>`;
    var a = apercu;
    if (!a) return html`<span class="ap-resume-etat">&nbsp;</span>`;
    if (a.rien === 'matieres') return html`<span class="puce puce-warn">Aucune matière cochée</span><span class="ap-resume-aide">Coche au moins une matière pour que l'ordonnanceur ait de quoi travailler.</span>`;
    if (a.rien === 'matieres2') return html`<span class="puce puce-warn">Deuxième planning sans matière</span><span class="ap-resume-aide">Choisis ses matières, ou décoche le deuxième planning.</span>`;
    if (a.erreur) return html`<span class="puce puce-danger">Aperçu indisponible</span><span class="ap-resume-aide">${a.erreur}</span>`;
    var bouts = [pluriel(a.items, 'item', 'items'), nombre(a.neuves) + (a.neuves > 1 ? ' séances' : ' séance')];
    if (a.debut) bouts.push('du ' + window.formatDateFr(a.debut) + ' au ' + window.formatDateFr(a.fin));
    var n = (state.tasks || []).length;
    // Un planning qui démarre avant aujourd'hui pose des séances déjà périmées : l'écran le dit ici, là où il
    // promet ce qui va être produit, et le champ « Date de départ » porte le même plancher.
    var passe = a.debut && a.debut < window.todayStr();
    return html`<span class="ap-resume-chiffres num">${bouts.join(' · ')}</span>
        <span class="ap-resume-aide">${state.appendMode
            ? (n ? 'Ajoutées aux ' + nombre(n) + ' séances déjà planifiées, soit ' + nombre(a.total) + ' en tout.'
                 : 'Le planning est vide : ce sont les seules.')
            : (n ? 'Elles remplacent les ' + nombre(n) + ' séances actuelles.'
                 : 'Le planning est construit de zéro.')}</span>
        ${passe ? html`<span class="ap-resume-alerte"><span class="puce puce-warn">Démarre le ${window.formatDateFr(a.debut)}, déjà passé</span><span class="ap-resume-aide">Avance la date de départ, ou ces séances arriveront en retard.</span></span>` : ''}
        ${a.enRetard.length ? html`<span class="ap-resume-alerte"><span class="puce puce-warn">${pluriel(a.enRetard.length, 'item trop tard', 'items trop tard')}</span><button type="button" class="lien-bouton" data-action="autoplanRetards">Pourquoi ?</button></span>` : ''}`;
}

function majResume() {
    var el = document.getElementById('ap-resume');
    if (el) el.innerHTML = String(resumeHtml());
    var b = document.getElementById('ap-lancer');
    if (b) b.setAttribute('aria-describedby', 'ap-resume');
}

// Débrayé : on redessine la ligne en « calcul en cours », puis on calcule une fois la salve de réglages retombée.
function programmerApercu(immediat) {
    clearTimeout(apercuMinuteur);
    // Revenu à des réglages déjà calculés (un rang remis à sa place, une case recochée) : le compte en cache est
    // le bon, et il faut retirer l'attente — sans ce faux, le rail restait sur « Calcul en cours » indéfiniment,
    // puisque le minuteur qui l'aurait levée vient d'être annulé.
    if (apercu && apercuCle === cleReglages()) { apercuEnCours = false; majResume(); return; }
    apercuEnCours = true; majResume();
    apercuMinuteur = setTimeout(function() {
        calculerApercu();
        apercuEnCours = false;
        majResume();
    }, immediat ? 0 : 180);
}

// ---------------------------------------------------------------- briques de rendu
// Un réglage : un titre, une icône, UNE phrase qui l'explique, ses champs. `large` prend toute la largeur de la
// grille (les listes : matières, ordre, deuxième planning, partiels), les autres se rangent deux par deux.
function reglage(id, icone, titre, phrase, corps, large) {
    return html`<div class="ap-reglage${large ? ' ap-reglage--large' : ''}" role="group" aria-labelledby="${id}">
        <h3 id="${id}">${ico(icone, { taille: 18 })}${titre}</h3>
        <p>${phrase}</p>
        ${corps}
    </div>`;
}

function section(id, titre, phrase, corps) {
    return html`<section class="ap-section" aria-labelledby="${id}">
        <h2 id="${id}">${titre}</h2>
        <p class="ap-section-phrase">${phrase}</p>
        <div class="ap-reglages">${corps}</div>
    </section>`;
}

function selecteur(id, libelle, cle, options, opts) {
    opts = opts || {};
    var courant = state[cle];
    return html`<div class="champ"><label for="${id}">${libelle}</label>
        <span class="select-enveloppe"><select id="${id}" class="select saisie" data-champ="${cle}"${opts.type ? html` data-type="${opts.type}"` : ''}${opts.redessine ? html` data-champ-rendu="modal"` : ''}>
            ${options.map(function(o) { return html`<option value="${o[0]}"${String(courant) === String(o[0]) ? html` selected` : ''}>${o[1]}</option>`; })}
        </select></span></div>`;
}

function interrupteur(id, cle, libelle, opts) {
    opts = opts || {};
    return html`<label class="ap-bascule" for="${id}">
        <input type="checkbox" id="${id}" data-champ="${cle}" data-type="bool"${opts.redessine ? html` data-champ-rendu="modal"` : ''}${state[cle] ? html` checked` : ''}>
        <span>${libelle}</span>
    </label>`;
}

// Le repère de famille : la teinte des six familles de la charte (`.mat mat-*`), réduite à une barre. Décoratif au
// sens strict — le nom de la matière est écrit juste après, la couleur ne porte jamais le sens seule.
function repereFamille(sp) {
    return html`<span class="ap-fam ${window.getSpecialtyStyle(sp)}" aria-hidden="true"></span>`;
}

// La grille des matières : une case de 44 px par matière, son repère de famille et son compte d'items.
function grilleMatieres(lot) {
    var toutes = window.allSpecialties || [], comptes = compteParMatiere();
    var choisies = lot === 2 ? specs2() : (state.selectedSpecs || []);
    var prefixe = lot === 2 ? 'ap-mat2-' : 'ap-mat-';
    return html`<div class="ap-matieres" role="group" aria-label="${lot === 2 ? 'Matières du deuxième planning' : 'Matières à planifier'}">
        ${toutes.map(function(sp, i) {
            var on = choisies.indexOf(sp) !== -1;
            return html`<label class="ap-matiere${on ? ' ap-matiere--on' : ''}" for="${prefixe + i}" data-matiere="${sp}">
                <input type="checkbox" id="${prefixe + i}"${on ? html` checked` : ''} data-change-action="${lot === 2 ? 'toggleSpecCheck2' : 'toggleSpecCheck'}" data-args="${attr([sp])}">
                ${repereFamille(sp)}
                <span class="ap-matiere-nom">${sp}</span>
                <span class="ap-matiere-n num">${comptes[sp] || 0}</span>
            </label>`;
        })}
    </div>`;
}

// L'ordre de priorité : un rang saisissable et deux chevrons, sur les actions historiques (moveSpec*).
function ordreMatieres(lot) {
    var visibles = window.ordreVisible(lot === 2 ? 2 : 1);
    if (!visibles.length) return html`<p class="ap-vide-ligne">Coche des matières : elles apparaîtront ici, dans l'ordre où l'ordonnanceur les posera.</p>`;
    var prefixe = lot === 2 ? 'ap-rang2-' : 'ap-rang-';
    // La classe dit de quel planning est la liste : les deux ordres se règlent séparément, et rien ne doit
    // permettre de les confondre — ni à l'œil, ni au test.
    return html`<ol class="ap-ordre ap-ordre--lot${lot === 2 ? 2 : 1}">${visibles.map(function(sp, i) {
        var premier = i === 0, dernier = i === visibles.length - 1;
        return html`<li class="ap-ordre-ligne">
            <label class="sr-only" for="${prefixe + i}">Rang de ${sp}</label>
            <input id="${prefixe + i}" class="saisie num ap-ordre-rang" type="number" min="1" max="${visibles.length}" inputmode="numeric" value="${i + 1}" data-change-action="autoplanRang" data-args="${attr([sp, lot === 2 ? 2 : 1])}">
            ${repereFamille(sp)}
            <span class="ap-ordre-nom">${sp}</span>
            <span class="ap-ordre-fleches">
                <button type="button" class="ico-btn ico-btn-sm" data-action="autoplanMonter" data-args="${attr([sp, lot === 2 ? 2 : 1])}"${premier ? html` disabled` : ''} aria-label="Monter ${sp} d'un rang">${ico('caret-up', { taille: 16 })}</button>
                <button type="button" class="ico-btn ico-btn-sm" data-action="autoplanDescendre" data-args="${attr([sp, lot === 2 ? 2 : 1])}"${dernier ? html` disabled` : ''} aria-label="Descendre ${sp} d'un rang">${ico('caret-down', { taille: 16 })}</button>
            </span>
        </li>`;
    })}</ol>`;
}

// ---------------------------------------------------------------- les bandes de l'écran
function railHaut() {
    var choisi = state.selectedAutoPlanPresetId;
    var actuel = choisi ? window.loadAutoPlanPresetsLocal().find(function(p) { return p.id === choisi; }) : null;
    return html`<header class="ap-rail">
        <button type="button" class="ico-btn" data-action="fermerAutoplan" aria-label="Fermer sans planifier">${ico('x')}</button>
        <h1 id="ap-titre" class="ap-titre">Planifier</h1>
        ${actuel ? html`<span class="ap-rail-preset">${actuel.name}</span>` : ''}
        <button type="button" class="ico-btn" data-action="autoplanMenuPresets" aria-haspopup="menu" aria-expanded="false" aria-label="Configurations enregistrées">${ico('floppy-disk')}</button>
    </header>`;
}

// Les configurations enregistrées : un menu (feuille au doigt), pas un sélecteur tronqué dans le rail. Le rail
// d'un écran de composition ne porte que le titre et la sortie ; le reste s'ouvre à la demande.
actions.autoplanMenuPresets = function(el) {
    var presets = window.loadAutoPlanPresetsLocal(), choisi = state.selectedAutoPlanPresetId;
    var items = presets.map(function(p) {
        return { label: p.name, icone: p.builtin ? 'package' : 'floppy-disk', courant: p.id === choisi,
            action: function() { window.actions.applyAutoPlanPreset(p.id); if (window.toast) window.toast('Configuration « ' + p.name + ' » appliquée'); } };
    });
    if (choisi) items.push({ label: 'Repartir des réglages en cours', icone: 'arrow-counter-clockwise', action: function() { window.actions.applyAutoPlanPreset(''); } });
    if (items.length) items.push({ sep: true });
    items.push({ label: 'Enregistrer ces réglages…', icone: 'plus', action: function() { actions.autoplanEnregistrerPreset(el); } });
    var courant = presets.find(function(p) { return p.id === choisi; });
    if (courant && !courant.builtin) items.push({ label: 'Supprimer « ' + courant.name + ' »', icone: 'trash-simple', danger: true, action: function() { actions.autoplanSupprimerPreset(); } });
    ouvrirMenu(el, items, { label: 'Configurations enregistrées', feuille: true });
};

function carteIA() {
    var ia = state.planningIA, enCours = !!state.planningIAEnCours;
    var ouvert = iaOuvert || enCours || !!ia || !!(state.planningBesoins || '').trim();
    return html`<div class="ap-ia carte">
        <button type="button" class="ap-ia-tete" data-action="autoplanIA" aria-expanded="${ouvert ? 'true' : 'false'}" aria-controls="ap-ia-corps">
            ${ico('sparkle', { taille: 18 })}
            <span class="ap-ia-titre">Laisser l'IA régler l'écran</span>
            <span class="ap-ia-aide">Décris tes besoins, elle pose les champs, tu relis.</span>
            ${ico(ouvert ? 'caret-up' : 'caret-down', { taille: 16, classe: 'ap-ia-chevron' })}
        </button>
        ${ouvert ? html`<div id="ap-ia-corps" class="ap-ia-corps">
            <div class="champ">
                <label for="ap-besoins">Tes besoins, en clair</label>
                <textarea id="ap-besoins" rows="3" data-champ="planningBesoins" data-live placeholder="Partiel de gynéco le 20 décembre, je veux être à J30 ce jour-là ; 2 h par jour en semaine, 4 h le week-end ; pédiatrie en renfort ; pas plus de 6 séances par jour.">${state.planningBesoins || ''}</textarea>
                <span class="aide">Elle ne fabrique aucune séance : elle règle les champs de cet écran, explique ses choix, et c'est toi qui lances.</span>
            </div>
            <div class="ap-ia-pied">
                <span class="ap-ia-modele">${ia && ia.modele ? 'Réglé par ' + ia.modele : ''}</span>
                <button type="button" class="btn btn-secondaire" data-action="autoplanIALancer"${enCours ? html` disabled aria-busy="true"` : ''}>${enCours ? 'Réglage en cours…' : 'Régler avec l’IA'}</button>
            </div>
            ${ia && ia.erreur ? html`<p class="ap-ia-erreur" role="alert">${ia.erreur}</p>` : ''}
            ${ia && ia.explication ? html`<div class="ap-ia-retour" role="status"><p><b>Ce que l'IA a réglé :</b> ${ia.explication}</p>
                ${(ia.avertissements || []).length ? html`<ul>${ia.avertissements.map(function(a) { return html`<li>${a}</li>`; })}</ul>` : ''}</div>` : ''}
        </div>` : ''}
    </div>`;
}

function sectionQuoi() {
    var toutes = window.allSpecialties || [], choisies = state.selectedSpecs || [];
    var candidats = window.regroupCandidats ? window.regroupCandidats(seuil()) : [];
    var archives = Object.keys(state.subPartsArchive || {});
    var scriptsAFaire = window.estAdmin && window.estAdmin() && window.lotPodcastsRegroupes ? window.lotPodcastsRegroupes() : [];
    return section('ap-quoi', 'Quoi planifier', 'Les matières que l’ordonnanceur va poser, et dans quel ordre il les prend.', html`
        ${reglage('ap-matieres-t', 'book-open', 'Matières', html`${pluriel(choisies.length, 'matière cochée', 'matières cochées')} sur ${toutes.length}. Un item compte pour sa matière principale et pour ses collèges secondaires : le décocher partout, c’est le sortir du planning.`, html`
            <div class="ap-matieres-actions">
                <button type="button" class="lien-bouton" data-action="selectAllSpecs">Tout cocher</button>
                <button type="button" class="lien-bouton" data-action="deselectAllSpecs">Tout décocher</button>
            </div>
            ${grilleMatieres(1)}`, true)}
        ${reglage('ap-ordre-t', 'list-numbers', 'Ordre de priorité', 'À charge égale, l’ordonnanceur commence par le haut de cette liste. Saisis un rang pour déplacer une matière d’un coup, ou utilise les chevrons.', ordreMatieres(1), true)}
        ${reglage('ap-lot2-t', 'stack', 'Deuxième planning', 'Un second lot posé en même temps, avec ses propres matières et sa propre date de départ. Il partage les plafonds du premier ; un item présent dans les deux va au premier.', html`
            ${interrupteur('ap-lot2', 'deuxiemeLot', 'Poser un deuxième planning en même temps', { redessine: true })}
            ${state.deuxiemeLot ? html`<div class="ap-lot2">
                <div class="champ ap-champ-court"><label for="ap-date2">Sa date de départ</label><input id="ap-date2" class="saisie" type="date" min="${window.todayStr()}" value="${state.configStartDate2 || ''}" data-champ="configStartDate2"></div>
                ${interrupteur('ap-pedia2', 'doublePediatrie2', 'Renforcement pédiatrie pour ce deuxième planning')}
                <p class="ap-note">Avec « un jour sur deux » plus bas et une date décalée d’un jour, les deux plannings ne se croisent jamais.</p>
                <div class="ap-matieres-actions">
                    <button type="button" class="lien-bouton" data-action="autoplanToutLot2" data-args="${attr([true])}">Tout cocher</button>
                    <button type="button" class="lien-bouton" data-action="autoplanToutLot2" data-args="${attr([false])}">Tout décocher</button>
                    <span class="ap-matieres-compte num">${pluriel(specs2().length, 'matière cochée', 'matières cochées')}</span>
                </div>
                ${grilleMatieres(2)}
                ${specs2().length ? html`<div class="champ">
                    <label for="ap-ordre2-preset">Reprendre l’ordre d’une configuration (ce planning seulement)</label>
                    <span class="select-enveloppe"><select id="ap-ordre2-preset" class="select saisie" data-change-action="autoplanOrdre2">
                        <option value="">Ordre actuel</option>
                        ${window.loadAutoPlanPresetsLocal().map(function(p) { return html`<option value="${p.id}"${state.presetOrdre2Id === p.id ? html` selected` : ''}>${p.name}</option>`; })}
                    </select></span>
                </div>${ordreMatieres(2)}` : ''}
            </div>` : ''}`, true)}
        ${reglage('ap-sp-t', 'scissors', 'Sous-parties', 'Un item scindé dans le catalogue peut être revu sous-partie par sous-partie, une par jour, plutôt qu’en une seule séance.', html`
            ${interrupteur('ap-sp', 'useSubPartsInAutoPlan', 'Planifier une sous-partie par jour pour les items scindés', { redessine: true })}
            ${state.useSubPartsInAutoPlan ? html`
                ${selecteur('ap-bloc', 'Revoir l’item en un seul bloc à partir de', 'blocDepuisJ', [[0, 'jamais'], [14, 'J14'], [21, 'J21'], [30, 'J30'], [45, 'J45'], [60, 'J60']], { type: 'int' })}
                <p class="ap-note">Les premières révisions restent par sous-partie ; les tardives portent sur l’item entier, en une seule séance.</p>` : ''}
            <div class="ap-regroup">
                <label class="ap-regroup-ligne" for="ap-seuil">Regrouper les items scindés de moins de
                    <input id="ap-seuil" class="saisie num ap-champ-nombre" type="number" min="200" max="20000" step="100" value="${seuil()}" data-change-action="autoplanSeuil"> mots en un seul bloc</label>
                <div class="ap-regroup-actions">
                    <button type="button" class="btn btn-secondaire btn-sm" data-action="regrouperSousParties" data-args="${attr([seuil()])}"${candidats.length ? '' : html` disabled`}>Regrouper (${candidats.length})</button>
                    ${archives.length ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="retablirSousParties">Rétablir les découpages (${archives.length})</button>` : ''}
                </div>
                <p class="ap-note">Le découpage est gardé en mémoire, les podcasts des sous-parties restent, les séances faites ne bougent pas.</p>
                ${scriptsAFaire.length ? html`<p class="ap-note">${pluriel(scriptsAFaire.length, 'item regroupé a', 'items regroupés ont')} des scripts de sous-parties sans script complet. <button type="button" class="lien-bouton" data-action="autoplanScriptsComplets">Écrire les scripts complets en lot</button></p>` : ''}
            </div>`, true)}
        ${reglage('ap-pedia-t', 'baby', 'Renforcement pédiatrie', html`Pour chaque item dont la pédiatrie est un collège secondaire, une seconde séquence de séances sur l’angle de l’enfant, à partir du même jour. Elles portent l’aimant <span class="puce puce-pedia">Renforcement pédiatrie</span>.`, interrupteur('ap-pedia', 'doublePediatrie', 'Doubler les items à collège pédiatrique'))}
    `);
}

function sectionRythme() {
    var exams = window.activeExams ? window.activeExams() : [];
    var liste = window.parseJListe(state.spacingCommun).map(function(d) { return 'J' + d; }).join(' ');
    return section('ap-rythme', 'À quel rythme', 'La cadence des nouveautés, l’écart entre deux révisions du même item, et ce qu’un jour peut absorber.', html`
        ${reglage('ap-charge-t', 'stack', 'Nouveautés par jour', 'Le nombre d’items découverts (J0) par journée de travail. Leurs révisions s’ajoutent ensuite par-dessus, jour après jour.', html`
            <div class="champ ap-champ-court"><label for="ap-parjour">Items découverts par jour</label><input id="ap-parjour" class="saisie num ap-champ-nombre" type="number" min="1" max="15" inputmode="numeric" value="${state.itemsPerDay}" data-champ="itemsPerDay" data-type="int"></div>
            ${selecteur('ap-pas', 'Rythme des découvertes', 'pasJ0', [[1, 'Chaque jour'], [2, 'Un jour sur deux']], { type: 'int' })}
            <p class="ap-note">Un jour sur deux, la suite des J devient paire (2, 4, 8, 14, 22, 30, 46, 60) : deux plannings démarrés à un jour d’écart ne se croisent jamais.</p>`)}
        ${reglage('ap-espace-t', 'clock', 'Écart entre deux révisions', 'La suite des J d’un item. Par défaut elle suit son score : mal su, il revient plus souvent.', html`
            ${selecteur('ap-spacing', 'Règle des J', 'spacingMode', [['score', 'Selon le score de l’item'], ['commun', 'La même suite pour tous les items']], { redessine: true })}
            ${state.spacingMode === 'commun' ? html`
                <div class="champ"><label for="ap-spacing-liste">La suite, en jours</label><input id="ap-spacing-liste" class="saisie" type="text" value="${state.spacingCommun || ''}" placeholder="1, 3, 7, 14, 30, 60" data-champ="spacingCommun" data-champ-rendu="modal">
                    <span class="aide num">${liste ? 'Lue comme : ' + liste : 'Illisible : la règle du score sera utilisée.'}</span></div>`
                : html`<p class="ap-note num">Score 3 : J1 J3 J7 J14 J21 J30 J45 J60 · score 2 : J2 J5 J10 J20 J30 J60 · et ainsi de suite.</p>`}
            ${selecteur('ap-maxj', 'Aller jusqu’à', 'maxJLimit', [[60, 'J60, la séquence entière'], [30, 'J30, plafond moyen'], [14, 'J14, plafond court']], { type: 'int' })}`)}
        ${reglage('ap-plafonds-t', 'sliders-horizontal', 'Plafonds par jour', 'Ce qu’une journée accepte au maximum. Un J0 n’est posé que si le jour a encore de la place ; un lissage final règle le reste. 0 = aucun plafond.', html`
            <div class="ap-plafonds">
                <div class="champ"><label for="ap-cap-tout">Tout compris</label><input id="ap-cap-tout" class="saisie num ap-champ-nombre" type="number" min="0" max="60" inputmode="numeric" value="${state.chargeTotaleParJour || 0}" data-champ="chargeTotaleParJour" data-type="int"></div>
                <div class="champ"><label for="ap-cap-j0">Nouveautés</label><input id="ap-cap-j0" class="saisie num ap-champ-nombre" type="number" min="0" max="60" inputmode="numeric" value="${state.plafondJ0ParJour || 0}" data-champ="plafondJ0ParJour" data-type="int"></div>
                <div class="champ"><label for="ap-cap-rev">Révisions</label><input id="ap-cap-rev" class="saisie num ap-champ-nombre" type="number" min="0" max="60" inputmode="numeric" value="${state.plafondRevisionsParJour || 0}" data-champ="plafondRevisionsParJour" data-type="int"></div>
            </div>`)}
        ${reglage('ap-boucle-t', 'arrow-clockwise', 'Boucle de révision', 'Une fois la séquence finie, l’item continue de revenir à intervalle régulier au lieu de sortir du planning. À garder pour l’année entière.', interrupteur('ap-boucle', 'isLoopMode', 'Faire boucler les items après leur dernier J'))}
        ${reglage('ap-tri-t', 'list-bullets', 'Ce qui passe en premier', 'À rang égal dans la liste des matières, ce qui départage deux items.', selecteur('ap-tri', 'Départager par', 'sortStrategy', [['score', 'Le rang de l’item d’abord'], ['specialty', 'L’ordre des matières d’abord']]))}
        ${exams.length ? reglage('ap-partiels-t', 'target', 'Partiels', 'Les items de ces matières passent en tête, assez tôt pour avoir atteint le J demandé le jour du partiel. Le résumé prévient si c’est impossible.', html`
            <ul class="ap-partiels">${exams.map(function(e, i) {
                return html`<li class="ap-partiel">
                    <label for="ap-partiel-${i}"><span class="ap-partiel-nom">${e.name || 'Partiel'}</span> <span class="ap-partiel-date num">${window.formatDateFr(e.date)}</span></label>
                    <span class="select-enveloppe"><select id="ap-partiel-${i}" class="select saisie" data-change-action="autoplanJCible" data-args="${attr([e.id])}">
                        ${[[0, 'sans contrainte'], [7, 'à J7'], [14, 'à J14'], [21, 'à J21'], [30, 'à J30'], [45, 'à J45'], [60, 'à J60']].map(function(o) { return html`<option value="${o[0]}"${(e.jCible || 0) === o[0] ? html` selected` : ''}>${o[1]}</option>`; })}
                    </select></span>
                </li>`;
            })}</ul>`) : ''}
    `);
}

function sectionQuand() {
    var n = (state.tasks || []).length;
    return section('ap-quand', 'À partir de quand', 'Le premier jour posé, et ce qu’il advient du planning actuel.', html`
        ${reglage('ap-depart-t', 'calendar-dots', 'Premier jour', 'Le jour du premier J0. Les révisions se comptent à partir de là.', html`
            <div class="champ ap-champ-court"><label for="ap-date">Date de départ</label><input id="ap-date" class="saisie" type="date" min="${window.todayStr()}" value="${state.configStartDate || ''}" data-champ="configStartDate"></div>`)}
        ${reglage('ap-existant-t', 'arrow-counter-clockwise', 'Le planning actuel', n ? html`Il compte ${nombre(n)} séances. Choisis ce qu’elles deviennent — les deux choix sont annulables par « Revenir en arrière ».` : 'Il est vide : il n’y a rien à remplacer.', html`
            <div class="ap-choix" role="radiogroup" aria-labelledby="ap-existant-t">
                <label class="ap-choix-ligne${!state.appendMode ? ' ap-choix-ligne--on' : ''}" for="ap-mode-remplacer">
                    <input type="radio" name="ap-mode" id="ap-mode-remplacer" value="remplacer"${!state.appendMode ? html` checked` : ''} data-change-action="autoplanMode" data-args="${attr(['remplacer'])}">
                    <span><b>Remplacer tout le planning</b><span class="ap-choix-aide">${n ? 'Les ' + nombre(n) + ' séances actuelles sont effacées, y compris celles déjà faites.' : 'Le planning est construit de zéro.'}</span></span>
                </label>
                <label class="ap-choix-ligne${state.appendMode ? ' ap-choix-ligne--on' : ''}" for="ap-mode-ajouter">
                    <input type="radio" name="ap-mode" id="ap-mode-ajouter" value="ajouter"${state.appendMode ? html` checked` : ''} data-change-action="autoplanMode" data-args="${attr(['ajouter'])}">
                    <span><b>Ajouter à la suite</b><span class="ap-choix-aide">Les séances actuelles restent en place ; seuls les items qui n’y sont pas encore sont posés.</span></span>
                </label>
            </div>`, true)}
    `);
}

function railBas() {
    return html`<footer class="ap-bas">
        <div class="ap-bas-inner">
            <p id="ap-resume" class="ap-resume" role="status" aria-live="polite">${resumeHtml()}</p>
            <div class="ap-bas-actions">
                <button type="button" class="btn btn-fantome" data-action="fermerAutoplan">Annuler</button>
                <button type="button" class="btn btn-primaire" id="ap-lancer" data-action="triggerAutoPlan" aria-describedby="ap-resume">${ico('magic-wand', { taille: 18 })}Lancer la planification</button>
            </div>
        </div>
    </footer>`;
}

// ---------------------------------------------------------------- l'écran
export function renderAutoplanModal() {
    // Le corps va être remplacé : on retient où on en était et sur quoi le focus se trouvait, pour les rendre après.
    var corps = document.querySelector('.ap-corps');
    if (corps) defilement = corps.scrollTop;
    var actif = document.activeElement;
    focusId = actif && actif.id && document.querySelector('.ap-ecran') && document.querySelector('.ap-ecran').contains(actif) ? actif.id : '';
    apresRendu();
    return html`<div class="ap-ecran" role="dialog" aria-modal="true" aria-labelledby="ap-titre">
        ${railHaut()}
        <div class="ap-corps">
            <div class="ap-colonne">
                ${carteIA()}
                ${sectionQuoi()}
                ${sectionRythme()}
                ${sectionQuand()}
            </div>
        </div>
        ${railBas()}
    </div>`;
}

// Après chaque redessin : la position du corps et le focus reviennent, le verrou de défilement est posé, et
// l'aperçu se remet à jour. Deux images d'attente pour laisser le navigateur poser la mise en page.
function apresRendu() {
    requestAnimationFrame(function() {
        var ecran = document.querySelector('.ap-ecran');
        if (!ecran) { document.body.classList.remove('ap-verrou'); return; }
        document.body.classList.add('ap-verrou');
        var corps = ecran.querySelector('.ap-corps');
        if (corps && defilement) corps.scrollTop = defilement;
        var cible = focusId ? document.getElementById(focusId) : null;
        if (cible && cible.focus) { try { cible.focus({ preventScroll: true }); } catch (e) { cible.focus(); } }
        else if (!ecran.contains(document.activeElement)) { var p = ecran.querySelector('.ico-btn'); if (p) p.focus(); }
        programmerApercu(!apercu);
    });
}

// ---------------------------------------------------------------- ouverture, fermeture, clavier
// Le déclencheur est retenu à la capture, avant toute délégation : le retour du focus à la fermeture ne dépend
// alors ni de l'ordre des imports ni de la version de `ouvrirAutoplan` qui l'emporte dans le registre.
document.addEventListener('click', function(ev) {
    var el = ev.target && ev.target.closest ? ev.target.closest('[data-action="ouvrirAutoplan"]') : null;
    if (el) ouvertureDepuis = el;
}, true);

actions.ouvrirAutoplan = function(el) {
    if (el && el.focus) ouvertureDepuis = el;
    else if (!ouvertureDepuis) ouvertureDepuis = document.activeElement;
    defilement = 0; focusId = '';
    compteParMatiere.cache = null;   // le catalogue a pu bouger depuis la dernière ouverture
    state.showAutoPlanModal = true;
    window.renderModal();
};
actions.fermerAutoplan = function() {
    if (!state.showAutoPlanModal) return;
    state.showAutoPlanModal = false;
    document.body.classList.remove('ap-verrou');
    clearTimeout(apercuMinuteur); apercuEnCours = false;
    window.renderModal();
    var retour = ouvertureDepuis;
    ouvertureDepuis = null;
    requestAnimationFrame(function() {
        var el = (retour && document.contains(retour)) ? retour : document.querySelector('[data-action="ouvrirAutoplan"]');
        if (el && el.focus) { try { el.focus(); } catch (e) {} }
    });
};
actions.autoplanIA = function() { iaOuvert = !iaOuvert; window.renderModal(); };

// Échap ferme ; Tab reste dans l'écran (il couvre l'application, qui est toujours dans le document).
document.addEventListener('keydown', function(ev) {
    if (!state.showAutoPlanModal || ev.defaultPrevented) return;
    var ecran = document.querySelector('.ap-ecran');
    if (!ecran) return;
    if (ev.key === 'Escape') {
        if (document.querySelector('dialog[open], .menu.ouvert')) return;   // le dialogue et le menu prennent Échap avant
        ev.preventDefault(); actions.fermerAutoplan(); return;
    }
    if (ev.key !== 'Tab') return;
    if (document.querySelector('dialog[open], .menu.ouvert')) return;
    var f = Array.prototype.filter.call(ecran.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'), function(el) {
        return !el.disabled && el.offsetParent !== null;
    });
    if (!f.length) return;
    var i = f.indexOf(document.activeElement);
    if (!ev.shiftKey && (i === -1 || i === f.length - 1)) { ev.preventDefault(); f[0].focus(); }
    else if (ev.shiftKey && i <= 0) { ev.preventDefault(); f[f.length - 1].focus(); }
}, true);

// Tout réglage touché dans l'écran refait le compte du rail bas, sans redessiner l'écran.
function surReglage(ev) {
    if (!state.showAutoPlanModal) return;
    var el = ev.target;
    if (!el || !el.closest || !el.closest('.ap-ecran')) return;
    programmerApercu(false);
}
document.addEventListener('change', surReglage);
document.addEventListener('input', surReglage);

// ---------------------------------------------------------------- actions propres à l'écran
actions.autoplanRang = function(spec, lot, el) { window.actions.moveSpecToPosition(spec, el.value, lot); };
// Adaptateurs : ces actions historiques attendent la valeur d'un champ en premier argument, que la délégation
// ne leur passe pas (data-args porte des constantes, l'élément arrive en dernier).
actions.autoplanOrdre2 = function(el) { window.actions.appliquerOrdrePreset2(el.value); };
actions.autoplanJCible = function(id, el) { window.actions.examSetJCible(id, el.value); };
actions.autoplanScriptsComplets = function() { window.actions.proposerScriptComplet(); };
// La garde du réglage par l'IA appartient à l'écran : sans besoins écrits, l'action historique ouvrait une alert()
// par-dessus le dialogue. Ici le champ vide se refuse comme les matières décochées — un toast et le focus au champ.
actions.autoplanIALancer = function() {
    if (!(state.planningBesoins || '').trim()) { refuser('#ap-besoins', 'Décris d’abord tes besoins'); return; }
    window.actions.planningIA();
};
actions.autoplanMonter = function(spec, lot) { window.actions.moveSpecUp(spec, lot); };
actions.autoplanDescendre = function(spec, lot) { window.actions.moveSpecDown(spec, lot); };
actions.autoplanMode = function(mode) { state.appendMode = mode === 'ajouter'; window.renderModal(); };
actions.autoplanToutLot2 = function(tout) {
    state.selectedSpecs2 = tout ? [].concat(window.allSpecialties) : [];
    window.renderModal();
};
actions.autoplanSeuil = function(el) {
    var n = parseInt(el.value, 10);
    state.regroupSeuilMots = (!n || n < 200) ? 3000 : Math.min(20000, n);
    window.renderModal();
};
actions.autoplanRetards = function() {
    var a = apercu;
    if (!a || !a.enRetard || !a.enRetard.length) return;
    var exemples = a.enRetard.slice(0, 6).map(function(it) { return '#' + it.id + ' ' + it.titre; }).join('\n');
    window.dialogue.alerter({
        titre: pluriel(a.enRetard.length, 'item arriverait trop tard', 'items arriveraient trop tard'),
        message: 'Avec ces réglages, ' + (a.enRetard.length > 1 ? 'ces items commencent' : 'cet item commence') + ' trop tard pour avoir atteint le J demandé le jour du partiel :\n\n' + exemples + (a.enRetard.length > 6 ? '\n…' : '') +
            '\n\nAugmente le nombre de nouveautés par jour, avance la date de départ, ou baisse le J cible du partiel.'
    });
};

// Les préréglages : l'enregistrement et la suppression passaient par prompt() / confirm() / alert().
actions.autoplanEnregistrerPreset = function(el) {
    window.ouvrirFormulaire(el, { titre: 'Enregistrer ces réglages', aide: 'Matières, ordre, charge, plafonds et rythme sont gardés dans ce navigateur.', label: 'Nom de la configuration', ok: 'Enregistrer' }).then(function(nom) {
        if (!nom) return;
        var presets = window.loadAutoPlanPresetsLocal(), s = state, id = 'preset_' + Date.now().toString(36);
        presets.push({ id: id, name: nom, config: {
            sortStrategy: s.sortStrategy, appendMode: s.appendMode, maxJLimit: s.maxJLimit, isLoopMode: s.isLoopMode,
            itemsPerDay: s.itemsPerDay, useSubPartsInAutoPlan: s.useSubPartsInAutoPlan, blocDepuisJ: s.blocDepuisJ || 0,
            chargeTotaleParJour: s.chargeTotaleParJour || 0, plafondJ0ParJour: s.plafondJ0ParJour || 0,
            plafondRevisionsParJour: s.plafondRevisionsParJour || 0, pasJ0: s.pasJ0 === 2 ? 2 : 1,
            spacingMode: s.spacingMode || 'score', spacingCommun: s.spacingCommun || '', doublePediatrie: s.doublePediatrie,
            selectedSpecs: [].concat(s.selectedSpecs), orderedSpecialties: [].concat(s.orderedSpecialties),
            deuxiemeLot: !!s.deuxiemeLot, doublePediatrie2: !!s.doublePediatrie2, selectedSpecs2: [].concat(specs2()),
            orderedSpecialties2: [].concat((s.orderedSpecialties2 || []).length ? s.orderedSpecialties2 : window.allSpecialties),
            configStartDate2: s.configStartDate2 || ''
        } });
        window.saveAutoPlanPresetsLocal(presets);
        state.selectedAutoPlanPresetId = id;
        window.renderModal();
        window.toast('Configuration « ' + nom + ' » enregistrée');
    });
};
actions.autoplanSupprimerPreset = function() {
    var id = state.selectedAutoPlanPresetId;
    if (!id || String(id).indexOf('builtin_') === 0) return;
    var p = window.loadAutoPlanPresetsLocal().find(function(x) { return x.id === id; });
    window.dialogue.confirmer({ titre: 'Supprimer cette configuration ?', message: '« ' + ((p && p.name) || id) +' » sera retirée de ce navigateur. Les réglages en cours ne changent pas.', ok: 'Supprimer', danger: true }).then(function(ok) {
        if (!ok) return;
        window.saveAutoPlanPresetsLocal(window.loadAutoPlanPresetsLocal().filter(function(x) { return x.id !== id; }));
        state.selectedAutoPlanPresetId = null;
        window.renderModal();
        window.toast('Configuration supprimée');
    });
};

// ---------------------------------------------------------------- le lancement
// Le calcul et l'enregistrement sont repris trait pour trait de l'ordonnanceur historique : même appel à
// construirePlanning (intouché : les snapshots de tests/snapshots/scheduler en font foi), même ordre, même payload
// save_tasks. Ce qui change, c'est la garde : le confirm() natif devient dialogue.confirmer, et l'avertissement sur
// les partiels est déjà passé par le résumé du rail bas au lieu de tomber en alert() après coup.
//
// La confirmation appartient à l'écran, pas au calcul : un appel programmatique (un test de non-régression, un
// script) garde la voie synchrone et n'ouvre aucune fenêtre.
function executerPlanification() {
    var resultat = window.construirePlanning(state, { items: window.ednsItems, now: Date.now });
    var nT = resultat.tasks;
    state.showAutoPlanModal = false;
    state.activeTab = 'calendrier';
    window.planningAction('Auto-planification');
    window.syncWithServer('save_tasks', { tasks: nT }, function() { window.renderApp(); });
    return resultat;
}

function refuser(selecteurCss, message) {
    if (window.toast) window.toast(message, { type: 'danger' });
    var el = document.querySelector(selecteurCss);
    if (el) { el.scrollIntoView({ block: 'center' }); if (el.focus) el.focus(); }
}

actions.triggerAutoPlan = function() {
    var c = state;
    if (!(c.selectedSpecs || []).length) { refuser('#ap-mat-0', 'Coche au moins une matière'); return; }
    if (c.deuxiemeLot && !specs2().length) { refuser('#ap-mat2-0', 'Choisis les matières du deuxième planning, ou décoche-le'); return; }
    if (!c.showAutoPlanModal) { executerPlanification(); return; }   // hors de l'écran : on planifie, sans garde

    var a = calculerApercu(), n = (c.tasks || []).length;
    var detail = (a && a.neuves) ? nombre(a.neuves) + (a.neuves > 1 ? ' s\u00e9ances vont \u00eatre pos\u00e9es' : ' s\u00e9ance va \u00eatre pos\u00e9e') + ' sur ' + pluriel(a.items, 'item', 'items') + '.' : '';
    var suite = n ? (c.appendMode
        ? ' Les ' + nombre(n) + ' s\u00e9ances actuelles restent en place.'
        : ' Les ' + nombre(n) + ' s\u00e9ances actuelles sont effac\u00e9es, y compris celles d\u00e9j\u00e0 faites.') : '';
    window.dialogue.confirmer({
        titre: c.appendMode ? 'Ajouter au planning ?' : 'Remplacer le planning ?',
        message: (detail + suite).trim() + '\n\n\u00ab Revenir en arri\u00e8re \u00bb annule l\u2019op\u00e9ration.',
        ok: c.appendMode ? 'Ajouter' : 'Remplacer',
        danger: !c.appendMode
    }).then(function(ok) {
        if (!ok) return;
        var resultat = executerPlanification();
        document.body.classList.remove('ap-verrou');
        ouvertureDepuis = null;
        window.renderModal();
        window.renderApp();   // l'\u00e9tat est d\u00e9j\u00e0 \u00e9crit : le calendrier arrive tout de suite, le serveur suit
        var enRetard = resultat.enRetard || [];
        if (enRetard.length) {
            apercu = apercu && !apercu.rien && !apercu.erreur ? apercu : {};
            apercu.enRetard = enRetard;
            window.toast(pluriel(enRetard.length, 'item de partiel arrive trop tard', 'items de partiel arrivent trop tard'),
                { type: 'danger', action: { label: 'D\u00e9tail', fn: function() { actions.autoplanRetards(); } } });
        } else {
            window.toast('Planning construit \u00b7 ' + nombre(resultat.tasks.length) + (resultat.tasks.length > 1 ? ' s\u00e9ances' : ' s\u00e9ance'));
        }
    });
};

window.renderAutoplanModal = renderAutoplanModal;
